import { Danger, TickCircle, Clock, Sparkles, Designtools } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type {
    PatientReportStatus,
    PatientTestAssignmentState,
    PatientTestConsolidationStatus,
    PatientSurveyStatus,
    PatientTestStatus,
} from '@/types/patient-overview';

export const SurveyStatusBadge = ({ status }: { status: PatientSurveyStatus }) => {
    const map: Record<
        string,
        {
            label: string;
            variant: 'warning' | 'success' | 'neutral';
        }
    > = {
        PENDING: {
            label: 'Pendente',
            variant: 'warning',
        },
        COMPLETED: {
            label: 'Concluído',
            variant: 'success',
        },
    };

    const config = map[status] || {
        label: status,
        variant: 'neutral' as const,
    };

    return <Badge variant={config.variant}>{config.label}</Badge>;
};

export const TestStatusBadge = ({
    status,
    assignmentState,
}: {
    status: PatientTestStatus;
    assignmentState?: PatientTestAssignmentState;
}) => {
    if (assignmentState === 'overdue') {
        return <Badge variant="warning">Prazo expirado</Badge>;
    }

    const map: Record<
        string,
        {
            label: string;
            variant: 'neutral' | 'info' | 'success';
        }
    > = {
        not_started: {
            label: 'Não iniciado',
            variant: 'neutral',
        },
        in_progress: {
            label: 'Em andamento',
            variant: 'info',
        },
        completed: {
            label: 'Concluído',
            variant: 'success',
        },
    };

    const config = map[status] || {
        label: status,
        variant: 'neutral' as const,
    };

    return <Badge variant={config.variant}>{config.label}</Badge>;
};

type ConsolidationBadgeConfig = {
    label: string;
    variant: 'info' | 'brand' | 'neutral' | 'warning';
    icon: typeof Clock;
    tooltip: string;
};

const getConsolidationBadgeConfig = (
    status: PatientTestConsolidationStatus | undefined,
    errorMessage?: string | null,
): ConsolidationBadgeConfig | null => {
    switch (status) {
        case 'queued':
            return {
                label: 'Gerando IA',
                variant: 'info',
                icon: Clock,
                tooltip: 'A IA está preparando este resultado.',
            };
        case 'ready':
            return {
                label: 'IA pronta',
                variant: 'brand',
                icon: Sparkles,
                tooltip: 'Pronto para revisão no laudo.',
            };
        case 'applied':
            return {
                label: 'Aplicada ao laudo',
                variant: 'neutral',
                icon: TickCircle,
                tooltip: 'Já adicionado ao laudo.',
            };
        case 'failed_usage_limit':
            return {
                label: 'Sem créditos',
                variant: 'warning',
                icon: Danger,
                tooltip: 'Consolidação automática indisponível.',
            };
        case 'failed_system':
            return {
                label: 'Falha na IA',
                variant: 'neutral',
                icon: Designtools,
                tooltip: errorMessage || 'Não foi possível consolidar este resultado.',
            };
        default:
            return null;
    }
};

export const TestConsolidationBadge = ({
    status,
    errorMessage,
}: {
    status?: PatientTestConsolidationStatus;
    errorMessage?: string | null;
}) => {
    const config = getConsolidationBadgeConfig(status, errorMessage);

    if (!config) {
        return null;
    }

    const Icon = config.icon;

    return (
        <TooltipProvider delayDuration={0}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Badge variant={config.variant} className="gap-1.5">
                        <Icon className="size-3" variant="Bulk" />
                        {config.label}
                    </Badge>
                </TooltipTrigger>
                <TooltipContent side="top">{config.tooltip}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const ReportStatusBadge = ({ status }: { status: PatientReportStatus }) => {
    const map: Record<
        string,
        {
            label: string;
            variant: 'info' | 'success' | 'neutral';
        }
    > = {
        in_progress: {
            label: 'Em andamento',
            variant: 'info',
        },
        completed: {
            label: 'Concluído',
            variant: 'success',
        },
    };

    const config = map[status] || {
        label: status,
        variant: 'neutral' as const,
    };

    return <Badge variant={config.variant}>{config.label}</Badge>;
};
